<template>
  <v-data-table
    v-model="selectedIds"
    :headers="headers"
    :sort-by="[{ key: 'timeCreated', order: 'desc' }]"
    :footer-props="{
      'items-per-page-all-text': 'Alle',
      'items-per-page-text': 'Buchungen pro Seite',
    }"
    items-per-page-text="Buchungen pro Seite"
    :items="bookings"
    item-value="id"
    tabindex="0"
    class="bg-accent elevation-1 accent-table"
    :loading="loading"
    :show-select="type === 'recurring'"
    loading-text="Daten werden geladen..."
    no-data-text="Keine Daten vorhanden"
    fixed-header
  >
    <template #loader>
      <v-progress-linear
        indeterminate
        color="primary"
        height="4"
      ></v-progress-linear>
    </template>
    <template v-slot:item.id="{ item }">
      <span
        class="text-primary"
        style="cursor: pointer"
        v-if="BookingPermissionService.allowUpdate(item)"
        ><a @click="onOpenBooking(item.id)">{{ item.id }}</a></span
      >
      <span v-else>{{ item.id }}</span>
    </template>
    <template v-slot:item.bookableIds="{ item }">
      <v-chip
        class="ml-1 my-1"
        color="secondary"
        variant="flat"
        v-for="(i, key) in item.bookableItems"
        :key="key"
        >{{ i._bookableUsed?.title }}</v-chip
      >
    </template>
    <template v-slot:item.timeBegin="{ item }">
      <span v-if="item.timeBegin">{{
        Intl.DateTimeFormat("de-DE", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(item.timeBegin))
      }}</span>
    </template>
    <template v-slot:item.timeEnd="{ item }">
      <span v-if="item.timeEnd">{{
        Intl.DateTimeFormat("de-DE", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(item.timeEnd))
      }}</span>
    </template>
    <template v-slot:item.timeCreated="{ item }">
      <span>{{
        Intl.DateTimeFormat("de-DE", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(item.timeCreated))
      }}</span>
    </template>
    <template v-slot:item.isCommitted="{ item }">
      <v-chip
        size="small"
        variant="flat"
        :color="
          item.isRejected
            ? 'error'
            : item.isCommitted
            ? 'success'
            : 'grey-lighten-2'
        "
      >
        <v-icon start size="small">
          {{
            item.isRejected
              ? "mdi-cancel"
              : item.isCommitted == true
              ? "mdi-check"
              : "mdi-timer-sand-empty"
          }}
        </v-icon>
        {{
          item.isRejected && !item.isCommitted
            ? "abgelehnt"
            : item.isRejected && item.isCommitted
            ? "stoniert"
            : item.isCommitted == true
            ? "freigegeben"
            : "ausstehend"
        }}
      </v-chip>
    </template>
    <template v-slot:item.isPayed="{ item }">
      <v-chip
        size="small"
        variant="flat"
        :color="item.isPayed ? 'success' : 'grey-lighten-2'"
      >
        <v-icon start size="small">
          {{ item.isPayed ? "mdi-check" : "mdi-timer-sand-empty" }}
        </v-icon>
        {{ item.isPayed ? "bezahlt" : "ausstehend" }}
      </v-chip>
    </template>
    <template v-slot:item.priceEur="{ item }">
      <span>{{
        Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
        }).format(item.priceEur)
      }}</span>
    </template>
    <template v-slot:item.paymentMethod="{ item }">
      <span>{{ translatePayMethod(item.paymentMethod) }}</span>
    </template>
    <template v-slot:item.recurringBookingId="{ item }">
      <span v-if="BookingPermissionService.allowUpdate(item)"
        ><a @click="onOpenRecurringBooking(item.recurringBookingId)">
          <span
            class="text-primary"
            style="cursor: pointer"
            v-if="
              item.recurringBookingId &&
              getRecurringCount(item.recurringBookingId) > 1
            "
          >
            {{ item.recurringBookingId }} ({{
              getRecurringCount(item.recurringBookingId)
            }})
          </span></a
        ></span
      >
      <span v-else>
        {{ item.recurringBookingId }}
        <span
          v-if="
            item.recurringBookingId &&
            getRecurringCount(item.recurringBookingId) > 1
          "
        >
          ({{ getRecurringCount(item.recurringBookingId) }} Wiederholungen)
        </span>
      </span>
    </template>
    <template v-slot:item.controls="{ item }">
      <span>
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon variant="text" v-bind="props" size="small">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              link
              @click="onOpenBooking(item.id)"
              :disabled="!BookingPermissionService.allowUpdate(item)"
              prepend-icon="mdi-information"
            >
              <v-list-item-title>Buchungsdetails ansehen</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              link
              @click="onOpenEditBooking(item.id)"
              :disabled="!BookingPermissionService.allowUpdate(item)"
              prepend-icon="mdi-pencil"
            >
              <v-list-item-title>Buchung bearbeiten</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="!item.isCommitted || item.isRejected"
              link
              @click="commitBooking(item.id)"
              :disabled="!BookingPermissionService.allowUpdate(item)"
              prepend-icon="mdi-checkbox-marked-circle"
            >
              <v-list-item-title>Buchung freigeben</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              link
              @click="rejectBooking(item.id)"
              :disabled="!BookingPermissionService.allowUpdate(item)"
              prepend-icon="mdi-close-circle"
            >
              <v-list-item-title>{{
                item.isCommitted ? "Buchung stornieren" : "Buchung ablehnen"
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              link
              @click="onOpenDeleteDialog(item.id)"
              class="text-red"
              :disabled="!BookingPermissionService.allowDelete(item)"
              prepend-icon="mdi-delete"
            >
              <v-list-item-title>Buchung löschen</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </span>
    </template>
  </v-data-table>
</template>

<script>
import BookingPermissionService from "@/services/permissions/BookingPermissionService";

export default {
  name: "BookingTable",
  computed: {
    BookingPermissionService() {
      return BookingPermissionService;
    },
  },
  props: {
    type: {
      type: String,
    },
    bookings: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    resetTrigger: {
      type: Number,
    },
  },
  data() {
    return {
      selectedIds: [],
      headers: [
        {
          title: "Id",
          align: "start",
          value: "id",
        },
        { title: "Buchungsobjekte", value: "bookableIds" },
        { title: "Von", value: "timeBegin", sortable: true },
        { title: "Bis", value: "timeEnd", sortable: true },
        { title: "Erstellt am", value: "timeCreated", sortable: true },
        { title: "Name", value: "name", sortable: true },
        { title: "Preis", value: "priceEur", sortable: true },
        { title: "Status", value: "isCommitted" },
        { title: "Zahlung", value: "isPayed" },
        { title: "Zahlungart", value: "paymentMethod" },
        ...(this.type === "single"
          ? [
              {
                title: "Wiederkehrende Buchung",
                value: "recurringBookingId",
                width: "170px",
              },
            ]
          : []),
        { title: "", value: "controls", sortable: false },
      ],
    };
  },
  watch: {
    selectedIds(newVal) {
      this.$emit("update:selected-ids", newVal);
    },
    resetTrigger() {
      this.selectedIds = [];
    },
  },
  methods: {
    translatePayMethod(paymentMethod) {
      switch (paymentMethod) {
        case "CASH":
          return "Bar";
        case "TRANSFER":
          return "Überweisung";
        case "CREDIT_CARD":
          return "Kreditkarte";
        case "DEBIT_CARD":
          return "EC-Karte";
        case "PAYPAL":
          return "PayPal";
        case "OTHER":
          return "Sonstiges";
        case "GIROPAY":
          return "Giropay";
        case "APPLE_PAY":
          return "Apple Pay";
        case "GOOGLE_PAY":
          return "Google Pay";
        case "EPS":
          return "EPS";
        case "IDEAL":
          return "iDEAL";
        case "MAESTRO":
          return "Maestro";
        case "PAYDIRECT":
          return "paydirekt";
        case "SOFORT":
          return "SOFORT-Überweisung";
        case "BLUECODE":
          return "Bluecode";
        default:
          return "Unbekannt";
      }
    },
    onOpenBooking(bookingId) {
      this.$emit("open-booking", bookingId);
    },
    onOpenRecurringBooking(recurringBookingId) {
      this.$router.push(`/admin/wiederkehrende-buchung/${recurringBookingId}`);
    },
    onOpenEditBooking(bookingId) {
      this.$emit("open-edit-booking", bookingId);
    },
    onOpenDeleteDialog(bookingId) {
      this.$emit("open-delete-dialog", bookingId);
    },
    commitBooking(bookingId) {
      this.$emit("commit-booking", bookingId);
    },
    rejectBooking(bookingId) {
      this.$emit("reject-booking", bookingId);
    },
    getRecurringCount(recurringBookingId) {
      return this.bookings.filter(
        (b) => b.recurringBookingId === recurringBookingId
      ).length;
    },
  },
};
</script>

<style></style>
